import { AppContext } from '@prisjakt/ui-app-context';
import { layout } from '@prisjakt/ui-base';
import {
  ButtonSize,
  IconButton as IconButtonComponent,
  ButtonIcon,
} from '@prisjakt/ui/button/variants';
import { IconCheckFilled, IconLock, IconPlus } from '@prisjakt/ui/icon';
import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UNTOGGLABLE_PROPERTY_COLUMNS } from 'src/areas/product-collection/utils/property-columns';
import { LayoutMode } from 'src/components/layout-buttons/types';
import { Tooltip, TooltipPlacement } from 'src/components/tooltip-new';
import Viewport from 'src/components/viewport';
import { AppState } from 'src/reducers';
import RouteContext from 'src/routes/context';
import styled from 'styled-components';
import { setProductCollectionLayout } from 'src/areas/product-collection/actions';
import { promptToast } from 'src/components/toast-container/actions';

export const TogglePropertyColumnButton = styled(IconButtonComponent)`
  padding: ${layout.measurements.spacing025};
`;

const selectProductLayout = (state: AppState) => {
  const { userSelectedLayout } = state.areas.productCollection;

  return userSelectedLayout;
};

type Props = {
  propertyId: string;
};

const TogglePropertyColumn = ({ propertyId }: Props) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const reduxDispatch = useDispatch();
  const { propertyColumnIds = [] } = getState();
  const propertyColumnIsActive = propertyColumnIds.indexOf(propertyId) !== -1;

  const productLayout = useSelector(selectProductLayout);

  const togglePropertyColumnIsActive = useCallback(() => {
    if (propertyColumnIsActive) {
      dispatch({ type: 'REMOVE_PROPERTY_COLUMN', propertyId });
    } else {
      dispatch({ type: 'ADD_PROPERTY_COLUMN', propertyId });
    }

    if (productLayout !== LayoutMode.TABLE) {
      reduxDispatch(setProductCollectionLayout(LayoutMode.TABLE));
      reduxDispatch(
        promptToast(
          'info',
          t('filter', 'properties.toggle-column-notification')
        )
      );
    }
  }, [
    propertyColumnIsActive,
    propertyId,
    dispatch,
    productLayout,
    reduxDispatch,
    t,
  ]);

  const disabled = UNTOGGLABLE_PROPERTY_COLUMNS.includes(propertyId);
  const togglePropertyColumnIcon = useMemo(() => {
    if (disabled) {
      return <IconLock />;
    }

    return propertyColumnIsActive ? <IconCheckFilled /> : <IconPlus />;
  }, [disabled, propertyColumnIsActive]);

  const title = t('filter', 'properties.toggle-column');

  return (
    <Viewport desktop>
      <Tooltip placement={TooltipPlacement.LEFT} title={title}>
        <TogglePropertyColumnButton
          onClick={togglePropertyColumnIsActive}
          containerProps={{
            title,
            'data-test': 'TogglePropertyColumnButton',
          }}
          size={ButtonSize.SMALL}
          disabled={disabled}
        >
          <ButtonIcon>{togglePropertyColumnIcon}</ButtonIcon>
        </TogglePropertyColumnButton>
      </Tooltip>
    </Viewport>
  );
};

export default TogglePropertyColumn;
